/**
 * RadioSelect Component
 * Single-select radio group inside a styled popover.
 * Matches Figma modal designs: olive radio circles, dark bg, consistent spacing.
 * Used for: Ritual Type, Repeat/Recurrence selectors.
 */

import { ChevronDown } from "lucide-react"
import { useState } from "react"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib"

export interface RadioOption {
  value: string
  label: string
}

interface RadioSelectProps {
  label: string
  options: RadioOption[]
  value?: string
  onChange?: (value: string) => void
  required?: boolean
  error?: string
  onClearError?: () => void
  placeholder?: string
  disabled?: boolean
  className?: string
  id?: string
}

export function RadioSelect({
  label,
  options,
  value,
  onChange,
  required,
  error,
  onClearError,
  placeholder = "Select...",
  disabled,
  className,
  id,
}: RadioSelectProps) {
  const [open, setOpen] = useState(false)

  const selectedLabel = options.find(o => o.value === value)?.label

  const handleSelect = (optionValue: string) => {
    onClearError?.()
    onChange?.(optionValue)
    setOpen(false)
  }

  return (
    <div className={cn("relative space-y-2", className)}>
      <Label
        className={cn(
          "text-xs leading-5 font-normal uppercase mb-[1px]",
          error ? "text-brand-terracotta-bright" : "text-body",
        )}
      >
        {label}
        {required && (
          <span className={error ? "text-brand-terracotta-bright" : "text-body"}>*</span>
        )}
      </Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            id={id}
            type="button"
            disabled={disabled}
            className={cn(
              "w-full pb-2 pt-1 ps-0 rounded-none bg-transparent text-sm font-semibold appearance-none focus:outline-none transition-all duration-200 border-b text-left flex items-center justify-between pr-2",
              error
                ? "border-brand-terracotta-bright text-brand-terracotta-bright"
                : "border-input text-[var(--color-chat-text)]",
              disabled && "opacity-50 cursor-not-allowed",
            )}
          >
            <span>{selectedLabel || placeholder}</span>
            <ChevronDown className={cn("size-4 transition-transform", open && "rotate-180")} />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={10}
          className="w-auto bg-popover border border-border rounded-[20px] p-[20px]"
        >
          <div className="flex flex-col gap-[15px]">
            {options.map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option.value)}
                className="flex items-center gap-[10px]"
              >
                <span className="size-[24px] rounded-full border-2 border-brand-olive flex items-center justify-center shrink-0">
                  {value === option.value && (
                    <span className="size-[12px] rounded-full bg-brand-olive" />
                  )}
                </span>
                <span
                  className={cn(
                    "text-[14px] leading-[25px] text-text-primary",
                    value === option.value ? "font-semibold" : "font-normal",
                  )}
                >
                  {option.label}
                </span>
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      {error && <p className="mt-1 text-xs text-brand-terracotta-bright">{error}</p>}
    </div>
  )
}
